import { IBooking, IUser, IUserAddress } from '../utils/entities/interface';
import { CustomerType } from '../utils/entities/type';
import { get, post } from './https'; // Make sure to adjust the import for the `get` function

interface Pagination {
  totalUsers: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface UserListResponse {
  status: boolean;
  data: IUser[];
  count: number;
  pagination: Pagination;
}

interface UserAddressesResponse {
  status: boolean;
  data: IUserAddress[];
}
const GETUsers = async ({
  page = 1,
  limit = 10,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
  startDate,
  endDate,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  startDate?: Date | null;
  endDate?: Date | null;
}): Promise<UserListResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/user/list?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  if (startDate && endDate) {
    url += `&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
  } else if (startDate) {
    url += `&startDate=${startDate.toISOString()}`;
  } else if (endDate) {
    url += `&endDate=${endDate.toISOString()}`;
  }

  // Fetch the response
  const response = await post(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch users');
  }
};

interface UserResponse {
  status: boolean;
  data: IUser;
}
const GETUser = async (userId: string): Promise<IUser> => {
  const url = `/api/v1/admin/user/${userId}`;
  const response = await get(url);
  if (response.status == 200) {
    return response.data.data;
  } else {
    throw Error('Error While Fetching User Details');
  }
};
const ToggleUserStatus = async (userId: string): Promise<number> => {
  // Call the API endpoint to toggle user active/inactive status
  const response = await post('/api/v1/admin/user/active-inactive', {
    userId,
  });

  console.log('response', response);
  if (response.status == 200) {
    return 200; // Return success message (User activated/deactivated)
  } else {
    throw new Error(response.statusText || 'Failed to change user status');
  }
};
const CreateUser = async ({
  phoneNumber,
  countryCode,
  userName,
  type,
  email,
  gender,
  profilePhoto,
}: {
  phoneNumber: string;
  countryCode: string;
  userName: string;
  type: CustomerType;
  email?: string; // Mark email as optional
  gender?: string;
  profilePhoto?: string;
}): Promise<number> => {
  const payload = {
    phoneNumber,
    countryCode,
    userName,
    type,
    ...(email && email.trim() !== '' ? { email } : {}), // Only include email if it's not an empty string
    ...(gender ? { gender } : {}),
    ...(profilePhoto ? { profilePhoto } : {}),
  };

  const response = await post('/api/v1/admin/user/add-edit', payload);

  if (response.status === 201) {
    return 201; // Return success message (User activated/deactivated)
  } else {
    throw new Error(response.statusText || 'Failed to create user');
  }
};

const EditUser = async ({
  phoneNumber,
  countryCode,
  userName,
  userId,
  type,
  email,
  gender,
  profilePhoto,
}: {
  phoneNumber: string;
  countryCode: string;
  userName: string;
  userId: string;
  type: CustomerType;
  email: string;
  gender?: string;
  profilePhoto?: string;
}): Promise<number> => {
  const response = await post('/api/v1/admin/user/add-edit', {
    phoneNumber,
    countryCode,
    userName,
    type,
    userId,
    ...(email && email.trim() !== '' ? { email } : {}),
    ...(gender ? { gender } : {}),
    ...(profilePhoto ? { profilePhoto } : {}),
  });
  if (response.status == 200) {
    return 200; // Return success message (User activated/deactivated)
  } else {
    throw new Error(response.statusText || 'Failed to create user');
  }
};
const GetAdresses = async (userId: string): Promise<UserAddressesResponse> => {
  const url = `/api/v1/user/address-list/${userId}`;

  const response = await get(url);
  if (response.status == 200) {
    return response.data;
  } else {
    throw new Error(response.statusText || 'Failed to Get user addresses');
  }
};

const UserBookings = async (userId: string): Promise<IBooking[]> => {
  const url = `/api/v1/admin/user/booking/list/${userId}`;
  const response = await get(url);
  if (response.status == 200) {
    return response.data.data;
  } else {
    throw Error('Fetching User Bookings Failed');
  }
};

interface CreateUserAddressProps {
  userId: string;
  houseNumber: string;
  street: string;
  state: string;
  city: string;
  zipCode: string;
  addressId: string;
  landmark: string;
  saveAs: 'HOME' | 'FRIEND' | 'OTHER' | 'WORK';
}

interface AddressResponse {
  status: number;
  data: IUserAddress;
}

// Function to add or edit a user address
const AddOrEditUserAddress = async (
  addressData: CreateUserAddressProps,
): Promise<AddressResponse> => {
  const url = '/api/v1/user/address-add-edit';
  const response = await post(url, addressData);

  if (response.status === 200 && response.data && response.data.data) {
    return {
      status: 201,
      data: response.data.data,
    };
  } else {
    throw new Error(response.statusText || 'Failed to add or edit address');
  }
};

export {
  GETUsers,
  ToggleUserStatus,
  CreateUser,
  GetAdresses,
  GETUser,
  EditUser,
  UserBookings,
  AddOrEditUserAddress,
};
